<div class="quotes-doc-card">
  <h2>Quotes Instructions</h2>
  <section id="overview" class="quotes-doc-section">
    <p>The Quotes page allows you to manage all your project quotes in one place. Here you can view, create, edit, duplicate, send, and export quotes, as well as use AI-powered assistants to streamline your workflow.</p>
    <ul class="quick-nav-list">
      <li><a (click)="scrollToSection('create-new-quote')">Create New Quote</a></li>
      <li><a (click)="scrollToSection('edit-quote')">Editing Quote Buttons</a></li>
      <li><a (click)="scrollToSection('table')">Table</a></li>
      <li><a (click)="scrollToSection('quotes-list-buttons')">Quotes List Buttons</a></li>
      <li><a (click)="scrollToSection('auto-fill-items')">Auto-fill Items</a></li>
      <li><a (click)="scrollToSection('use-assistant')">Use Assistant</a></li>
      <li><a (click)="scrollToSection('send-quote')">Send Quote</a></li>
    </ul>
  </section>

  <div class="quotes-sections-nav">
    <ul class="quotes-section-list">
    </ul>
  </div>

  <section id="quotes-list" class="quotes-doc-section">
    <h3>Quotes List</h3>
    <p>All your quotes are displayed in a list. You can filter, search, and select quotes to view their details. The list shows key information such as quote title, status, and last update.</p>
  </section>

  <section id="create-new-quote" class="quotes-doc-section">
    <h3>Create New Quote</h3>
    <p>There are two ways to create a new quote. Follow the steps below to get started:</p>
    <div class="create-quote-steps">
      <div class="step-item">
        <div class="create-quote-card">
          <div class="step-card-flex">
            <div class="step-number">1</div>
            <div class="step-main-content">
              <div class="step-content">
                <h4>From BOM Table</h4>
                <div class="step-image-top with-side-image">
                  <img src="assets/img/documentation-img/create-new.png" alt="Create new quote from BOM table" class="doc-image-new">
                  <p>When you view the BOM table of a specific model, there is a special button in the top button panel that will help you automatically create a quote based on the selected model.</p>
                </div>
              </div>
              <div class="step-video-toggle">
                <button class="btn-video" (click)="showNewQuoteVideo1 = !showNewQuoteVideo1">
                  {{ showNewQuoteVideo1 ? 'Hide video' : 'Show video' }}
                </button>
                <div *ngIf="showNewQuoteVideo1" class="video-container">
                  <video controls width="220">
                    <source src="assets/img/documentation-img/video/new-quote2.mp4" type="video/mp4">
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="step-item">
        <div class="create-quote-card">
          <div class="step-card-flex">
            <div class="step-number">2</div>
            <div class="step-main-content">
              <div class="step-content">
                <h4>From Quotes Page</h4>
                <div class="step-image-top with-side-image">
                  <img src="assets/img/documentation-img/create-new2.png" alt="Create new quote from quotes page" class="doc-image-new2">
                  <p>On the Quotes page, there is a "Create new quote" button on the left side. Click on it and select the model for which you want to create a new quote.</p>
                </div>
              </div>
              <div class="step-video-toggle">
                <button class="btn-video" (click)="showNewQuoteVideo2 = !showNewQuoteVideo2">
                  {{ showNewQuoteVideo2 ? 'Hide video' : 'Show video' }}
                </button>
                <div *ngIf="showNewQuoteVideo2" class="video-container">
                  <video controls width="220">
                    <source src="assets/img/documentation-img/video/new-quote1.mp4" type="video/mp4">
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="step-item">
        <div class="create-quote-card">
          <div class="step-card-flex">
            <div class="step-main-content">
              <div class="step-content">
                <h4>Configure Quote Details</h4>
                <div class="step-image-top">
                  <img src="assets/img/documentation-img/to-re.png" alt="Quote configuration fields" class="doc-image-new3">
                  <div class="step-description">
                    <p>When you select a model for a quote, configuration fields will appear where you can fill in:</p>
                    <ul>
                      <li><strong>Quote Title:</strong> A descriptive name for your quote.</li>
                      <li><strong>TO:</strong> The recipient of the quote.</li>
                      <li><strong>RE:</strong> The subject or project description related to the quote.</li>
                    </ul>
                    <div class="important-note">
                      <strong>Important:</strong> After saving, these fields cannot be changed. These fields must be unique. If an error occurs when creating a new quote, it is better to change the values in the title, TO, RE fields or add a new version to an already created quote.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <section id="table" class="quotes-doc-section">
    <h3>Table</h3>
    <div class="create-quote-card">
      <div class="doc-flex-row">
        <div class="video-container">
          <video controls width="400">
            <source src="assets/img/documentation-img/video/table.mp4" type="video/mp4">
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
      <div class="button-descriptions">
        <ul>
          <li><strong>Selecting Items with Checkboxes:</strong> Use checkboxes to select which items will be added to your quote.</li>
          <li><strong>Category Navigation:</strong> Quickly switch between categories using the navigation bar for easy access to different item groups. This function is only available in the materials table.</li>
          <li><strong>Sidebar Checkbox Status:</strong> Sidebar checkboxes show as checked if at least one item in that category is added to the quote.</li>
          <li><strong>Show Selected Only:</strong> Use the "Show selected only" button to filter and view only the items you’ve already added to the quote.</li>
          <li><strong>Editing Prices:</strong> Change the price of any item by entering your own value in the special price field.</li>
          <li><strong>Automatic Price Calculation:</strong> All prices are calculated automatically as you make changes.</li>
          <li><strong>Keyboard Navigation:</strong> Easily switch between cells using the Tab key for faster data entry.</li>
          <li><strong>Source Tracking:</strong> Click the icon next to an item to see where the price was set from and follow the link to the product.</li>
          <li><strong>Bulk Price Import with Assistant:</strong> If you have your own price catalog, use the assistant to upload your file and automatically fill in prices.</li>
        </ul>
      </div>
    </div>
  </section>

  <section id="edit-quote" class="quotes-doc-section">
    <h3>Editing Quote Buttons</h3>
    <p>When editing a quote, you have access to several buttons that help you manage and update the quote details efficiently. Below is a guide to the main buttons available on the editing page.</p>
    <div id="edit-quote-buttons" class="quotes-list-buttons-card">
      <div class="doc-flex-row">
        <img src="assets/img/documentation-img/quotes-edit-buttons.png" alt="Editing Quote Buttons" class="doc-image">
        <div class="button-descriptions">
          <h4>Button Descriptions</h4>
          <ul>
            <li><strong>Auto-fill Items:</strong> If you have a file with ready-made tables, you can use it to quickly fill out your quote.</li>
            <li><strong>Use Assistant:</strong> This assistant will help you edit your tables faster. For example, delete certain rows or reduce all prices by 10%, etc. Just enter your request and the assistant will do everything possible.</li>
            <li><strong>Save Quote:</strong> Use this button to save the changes you have made to the quote.</li>
            <li><strong>Send Quote:</strong> Use this button to send the quote to a client. Please also note that the version that was selected was sent.</li>
            <li><strong>Add Table:</strong> Use this button to add a new table to the quote. You can add as many tables as you need.</li>
            <li><strong>Cancel:</strong> Use this button to cancel the changes you have made to the quote and return to the previous page.</li>
          </ul>
        </div>
      </div>
    </div>
  </section>

  <section id="quotes-list-buttons" class="quotes-doc-section">
    <h3>Quotes List Buttons</h3>
    <p>These are the buttons you can see when you are on the "Quotes" page. These are auxiliary buttons that will help you interact with the quote you have selected.</p>
    <div class="quotes-list-buttons-card">
      <div class="doc-flex-row">
        <img src="assets/img/documentation-img/quotes-list-buttons.png" alt="Quotes List Buttons" class="doc-image">
        <div class="button-descriptions">
          <h4>Button Descriptions</h4>
          <ul>
            <li><strong>Select version dropdown:</strong> This dropdown will allow you to select the version of the quote you want to see. You can select the version you want to see from the dropdown. Just click on it and all saved versions of it will appear quote. Select the version you need from the list and continue working with it.</li>
            <li><strong>Export PDF:</strong> Clicking on this button will automatically download a PDF file of your selected quote.</li>
            <li><strong>Visitors:</strong> Use this button to see who the selected quote was sent to, the quote statuses for each client, and what comments clients have written on this quote.</li>
            <li><strong>Edit:</strong> Use this button to edit the selected quote. You can't edit the quote title, TO and RE.</li>
            <li><strong>Add:</strong> Use this button to add a new version of the quote. You can't edit the quote title, TO and RE. You can change all other elements, add new ones or delete unnecessary ones and save them as a new version of the quote.</li>
            <li><strong>Send:</strong> Use this button to send the selected quote to a client. Please also note that the version that was selected was sent.</li>
            <li><strong>Delete:</strong> Use this button to delete the selected quote. Attention, all versions of this quote will be deleted, this action cannot be undone.</li>
          </ul>
        </div>
      </div>
    </div>
  </section>

  <section id="auto-fill-items" class="quotes-doc-section">
    <h3>Auto-fill Items</h3>
    <p>
      The <strong>Auto-fill Items</strong> feature allows you to quickly populate your quote tables using a file with pre-made data. This saves time and reduces manual entry errors. Simply upload your file, and the system will automatically fill in the relevant fields in your quote.
    </p>
    <div class="create-quote-card">
      <div class="doc-flex-row">
        <img src="assets/img/documentation-img/auto-fill-items.png" alt="Auto-fill Items Example" class="doc-image">
        <div class="button-descriptions">
          <ul>
            <li><strong>Step 1:</strong> Click the <strong>Auto-fill Items</strong> button while editing your quote.</li>
            <li><strong>Step 2:</strong> Upload your file containing the table data.</li>
            <li><strong>Step 3:</strong> Enter the necessary instructions for adding or modifying items as needed.</li>
            <li><strong>Step 4:</strong> Review the auto-filled fields and make any necessary adjustments.</li>
            <li><strong>Step 5:</strong> Save your quote to keep the changes.</li>
          </ul>
        </div>
      </div>
      <div class="step-video-toggle">
        <button class="btn-video" (click)="showAutoFillVideo = !showAutoFillVideo">
          {{ showAutoFillVideo ? 'Hide video' : 'Show video' }}
        </button>
        <div *ngIf="showAutoFillVideo" class="video-container">
          <video controls width="600">
            <source src="assets/img/documentation-img/video/auto-fill-items.mp4" type="video/mp4">
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
    </div>
  </section>

  <section id="use-assistant" class="quotes-doc-section">
    <h3>Use Assistant</h3>
    <p>
      The <strong>Use Assistant</strong> feature helps you edit your quote tables more efficiently by automating repetitive or complex tasks. You can instruct the assistant to perform actions such as deleting rows, adjusting prices, or making bulk changes, saving you time and effort.
    </p>
    <div class="create-quote-card">
      <div class="doc-flex-row">
        <img src="assets/img/documentation-img/use-assistant.png" alt="Use Assistant Example" class="doc-image doc-image-new">
        <div class="button-descriptions">
          <ul>
            <li><strong>Step 1:</strong> Click the <strong>Use Assistant</strong> button while editing your quote.</li>
            <li><strong>Step 2:</strong> Enter your instructions (e.g., "Delete all rows with price = 0" or "Reduce all prices by 10%.")</li>
            <li><strong>Step 3:</strong> Select, with the help of checkboxes, which tables you need to work with. This feature is not available for the Materials table.</li>
            <li><strong>Step 4:</strong> Review the changes suggested or made by the assistant.</li>
            <li><strong>Step 5:</strong> Make any additional adjustments if needed.</li>
            <li><strong>Step 6:</strong> Save your quote to keep the changes.</li>
          </ul>
        </div>
      </div>
      <div class="important-note">
        <strong>Tip:</strong> Be as specific as possible with your instructions for the best results.
      </div>
      <div class="step-video-toggle">
        <button class="btn-video" (click)="showAssistantVideo = !showAssistantVideo">
          {{ showAssistantVideo ? 'Hide video' : 'Show video' }}
        </button>
        <div *ngIf="showAssistantVideo" class="video-container">
          <video controls width="600">
            <source src="assets/img/documentation-img/video/use-assistant.mp4" type="video/mp4">
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
    </div>
  </section>

  <section id="send-quote" class="quotes-doc-section">
    <h3>Send Quote</h3>
    <p>
      The <strong>Send Quote</strong> feature allows you to send your completed quote directly to your clients. Follow these steps to use this feature efficiently:
    </p>
    <div class="create-quote-card">
      <div class="doc-flex-row">
        <img src="assets/img/documentation-img/send-quote.png" alt="Send Quote Example" class="doc-image doc-image-new">
        <div class="button-descriptions">
          <ul>
            <li><strong>Step 1:</strong> Click the <strong>Send Quote</strong> button on the button panel on the Quotes list page.</li>
            <li><strong>Step 2:</strong> Enter the client's email address.</li>
            <li><strong>Step 3:</strong> Click the <strong>Add</strong> button to add the client to the recipient list.</li>
            <li><strong>Step 4:</strong> Add a message or instructions for your clients.</li>
            <li><strong>Step 5:</strong> Confirm and send the quote. The selected version will be sent to the chosen recipients.</li>
            <li><strong>Step 6:</strong> You can track the status of your sent quotes and see when clients have viewed or commented on them.</li>
          </ul>
        </div>
      </div>
      <div class="important-note">
        <strong>Note:</strong> You can send a quote to several clients at once, but the message will be the same for all recipients.
      </div>
      <div class="email-example">
        <h6>Here you can see what the email sent to the client will look like.</h6>
        <img src="assets/img/documentation-img/email-test.png" alt="Email Example" class="email-example-img">
      </div>
    </div>
  </section>
</div>
